import { Link } from "react-router-dom";
import { Check, Crown, ArrowRight, BadgePercent } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { CashNotice } from "@/components/site/CashNotice";
import { useSiteSettings } from "@/context/SettingsContext";
import { money } from "@/lib/api";

const ACCENTS = {
  bronze: "from-orange-700 to-amber-600",
  silver: "from-slate-400 to-slate-600",
  gold: "from-amber-400 to-yellow-600",
  platinum: "from-emerald-400 via-purple-500 to-amber-400",
};

const TierCard = ({ tier, index }) => {
  const featured = tier.best_value;
  return (
    <Reveal delay={index * 0.08} className="h-full">
      <div className={`relative h-full rounded-3xl p-[2px] ${featured ? "bg-gradient-to-br from-amber-400 via-purple-500 to-emerald-500 lg:-translate-y-4" : "bg-border"}`} data-testid={`tier-card-${tier.key}`}>
        <div className="h-full rounded-[calc(1.5rem-2px)] bg-card p-7 flex flex-col hover:scale-[1.01] transition-transform">
          {featured && (
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-amber-400 text-[#0A1F13] px-3 py-1 text-xs font-extrabold" data-testid="tier-best-value-badge">
              <Crown className="h-3.5 w-3.5" /> Best Value
            </span>
          )}
          <div className={`h-2 w-16 rounded-full bg-gradient-to-r ${ACCENTS[tier.key]}`} />
          <span className="mt-4 self-start inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-600 dark:text-emerald-300 px-2.5 py-0.5 text-xs font-extrabold" data-testid={`tier-zero-commission-${tier.key}`}>
            <BadgePercent className="h-3.5 w-3.5" /> 0% Commission
          </span>
          <h3 className="mt-4 text-2xl font-bold">{tier.name}</h3>
          <p className="mt-1 text-muted-foreground font-semibold">{tier.intros_label}</p>
          <p className="mt-6 font-display text-4xl font-extrabold" data-testid={`tier-price-${tier.key}`}>{money(tier.price)}</p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mt-1">One-time · Cash only</p>
          <ul className="mt-6 space-y-2 text-sm flex-1">
            {["In-person farm introductions", "Screened & vetted network", "No monthly or annual fees"].map((f) => (
              <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5" />{f}</li>
            ))}
          </ul>
          <Link
            to={`/apply?tier=${tier.key}`}
            data-testid={`tier-apply-button-${tier.key}`}
            className={`group mt-8 inline-flex h-12 items-center justify-center gap-2 rounded-full font-bold transition-[transform,background-color] hover:-translate-y-0.5 ${featured ? "bg-primary text-primary-foreground btn-glow" : "bg-secondary hover:bg-primary hover:text-primary-foreground"}`}
          >
            Apply for this tier <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </Reveal>
  );
};

export const Tiers = () => {
  const { settings } = useSiteSettings();
  return (
    <section id="tiers" className="py-24 sm:py-32" data-testid="tiers-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="eyebrow text-amber-500">Membership tiers</p>
            <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">Pay once. Get introduced.</h2>
            <p className="mt-4 text-muted-foreground text-base sm:text-lg">One-time membership fee. No monthly or annual fees, ever.</p>
          </div>
          <CashNotice className="lg:max-w-sm" testId="tiers-cash-notice" />
        </Reveal>
        <Reveal className="mt-12">
          <div className="rounded-3xl p-[2px] bg-gradient-to-r from-emerald-500 via-amber-400 to-purple-500" data-testid="tiers-no-commission-banner">
            <div className="rounded-[calc(1.5rem-2px)] bg-card px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="h-11 w-11 shrink-0 rounded-2xl bg-gradient-to-br from-emerald-500 to-amber-400 grid place-items-center"><BadgePercent className="h-5 w-5 text-white" /></span>
              <p className="font-display text-lg sm:text-2xl font-bold">One fee. Zero commissions. Every dollar you spend after goes to your product.</p>
            </div>
          </div>
          <p className="mt-4 text-center sm:text-left text-muted-foreground font-semibold" data-testid="tiers-quality-line">Every introduction is to a farm growing clean, dispensary-grade flower, and we'll help you get independent lab testing.</p>
        </Reveal>
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(settings?.tiers || []).map((t, i) => <TierCard key={t.key} tier={t} index={i} />)}
        </div>
      </div>
    </section>
  );
};
